import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  DEALER_PORTAL_BANNER_SPECS,
  DealerPortalBanner,
  dealerPortalBannerShape,
} from '@jfw/types';

export type DealerDashboardHeroBanner = Pick<
  DealerPortalBanner,
  'imageUrl' | 'altText' | 'clickRoute' | 'targetEcomStatuses'
>;

export type DealerDashboardBannerLayout =
  'standard' | 'standard-centered' | 'referral';

/**
 * EXT-6167: which fixed-ratio box the CMS banner sits in. A referral-shaped
 * (O-only) banner spans the row. A standard one sits beside the button
 * column for H/I; for an O dealer, who has no button column, it keeps the
 * standard ratio, capped and centered rather than stretched.
 */
export function dealerDashboardBannerLayout(
  banner: DealerDashboardHeroBanner | null | undefined,
  ecomStatus: string | null | undefined,
): DealerDashboardBannerLayout | null {
  if (!banner) {
    return null;
  }
  if (dealerPortalBannerShape(banner) === 'referral') {
    return 'referral';
  }
  return ecomStatus === 'O' ? 'standard-centered' : 'standard';
}

/** A full http(s) URL opens as a real link; anything else is an app route. */
export function isExternalBannerRoute(
  route: string | null | undefined,
): boolean {
  return /^https?:\/\//i.test(route ?? '');
}

/**
 * EXT-6571: the top of the newSite dealer dashboard -- title, CMS banner and
 * the button column -- shared so Platform Portal's banner screens can preview
 * exactly what a dealer sees. The dealer portal owns where the data comes
 * from and what View Your Balance does; this only lays it out.
 *
 * With `interactive` false (the admin preview) the whole hero is `inert`: its
 * links point at dealer-portal routes that don't exist in Platform Portal.
 */
@Component({
  selector: 'jfw-wholesale-dealer-dashboard-hero',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  templateUrl: './dealer-dashboard-hero.component.html',
  styleUrl: './dealer-dashboard-hero.component.scss',
  host: {
    '[attr.inert]': 'interactive() ? null : ""',
  },
})
export class DealerDashboardHeroComponent {
  public readonly banner = input<DealerDashboardHeroBanner | null>(null);
  public readonly ecomStatus = input<string | null | undefined>(null);
  public readonly interactive = input(true);

  public readonly balanceClick = output<void>();

  protected readonly title = computed(() =>
    this.ecomStatus() === 'O' ? 'Referral Dashboard' : 'Dashboard',
  );

  protected readonly showSideColumn = computed(() => this.ecomStatus() !== 'O');

  protected readonly layout = computed(() =>
    dealerDashboardBannerLayout(this.banner(), this.ecomStatus()),
  );

  /** The box's ratio comes from the same spec the upload guidance shows. */
  protected readonly aspectRatio = computed(() => {
    const layout = this.layout();
    if (!layout) {
      return null;
    }
    const { width, height } =
      DEALER_PORTAL_BANNER_SPECS[
        layout === 'referral' ? 'referral' : 'standard'
      ].display;
    return `${width} / ${height}`;
  });

  protected readonly centeredMaxWidth = `${DEALER_PORTAL_BANNER_SPECS.standard.display.width}px`;

  protected readonly isExternalUrl = isExternalBannerRoute;

  protected onBalance(): void {
    if (this.interactive()) {
      this.balanceClick.emit();
    }
  }
}
